import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EmailIcon from "@mui/icons-material/Email";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { SERVER_BASE_URL } from "../Config";

interface ProviderRequestData {
  id: number;
  status: string;
  createdAt: string;
  firstName: string;
  lastName: string;
  practiceName: string;
  npi: string;
  stateLicenses: string;
  street: string;
  city: string;
  state: string;
  zip: string;
  officePhone: string;
  mobilePhone: string;
  fax: string;
  email: string;
  salesRep: string;
  contact: string;
}

interface ApprovedProvider {
  id: number;
  name: string;
  email: string;
  temporary_password: string;
}

interface ProviderRequestsProps {
  onBack: () => void;
}

const statusChip = (status: string) => {
  switch (status) {
    case "PENDING":
      return <Chip label="Pending" color="warning" size="small" />;
    case "APPROVED":
      return <Chip label="Approved" color="success" size="small" />;
    case "REJECTED":
      return <Chip label="Rejected" color="error" size="small" />;
    default:
      return <Chip label={status} size="small" />;
  }
};

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZone: "America/New_York",
  });
};

const ProviderRequests: React.FC<ProviderRequestsProps> = ({ onBack }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [requests, setRequests] = useState<ProviderRequestData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState("PENDING");

  // Detail/approve dialog
  const [selectedRequest, setSelectedRequest] = useState<ProviderRequestData | null>(null);
  const [openDetailDialog, setOpenDetailDialog] = useState(false);

  // Password dialog after approval
  const [approvedProvider, setApprovedProvider] = useState<ApprovedProvider | null>(null);
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(
        `${SERVER_BASE_URL}/admin/provider-requests?status=${statusFilter}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pharmacistToken}`,
          },
        }
      );
      const data = await response.json();
      if (data.success) {
        setRequests(data.requests);
      } else {
        setError(data.message || "Failed to load requests");
      }
    } catch (err) {
      setError("Failed to load requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, [statusFilter]);

  const handleApprove = async () => {
    if (!selectedRequest) return;
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(
        `${SERVER_BASE_URL}/admin/provider-requests/${selectedRequest.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pharmacistToken}`,
          },
          body: JSON.stringify({ action: "approve" }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setApprovedProvider(data.provider);
        setEmailSent(!!data.email_sent);
        setOpenDetailDialog(false);
        setOpenPasswordDialog(true);
        setSuccess("Request approved!");
        loadRequests();
      } else {
        setError(data.message || "Failed to approve request");
      }
    } catch (err) {
      setError("Failed to approve request");
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest) return;
    // eslint-disable-next-line no-restricted-globals
    if (!confirm(`Reject request from ${selectedRequest.firstName} ${selectedRequest.lastName}?`)) return;
    try {
      setLoading(true);
      setError(null);
      const pharmacistToken = localStorage.getItem("pharmacistToken");
      const response = await fetch(
        `${SERVER_BASE_URL}/admin/provider-requests/${selectedRequest.id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${pharmacistToken}`,
          },
          body: JSON.stringify({ action: "reject" }),
        }
      );
      const data = await response.json();
      if (data.success) {
        setOpenDetailDialog(false);
        setSuccess("Request rejected");
        loadRequests();
      } else {
        setError(data.message || "Failed to reject request");
      }
    } catch (err) {
      setError("Failed to reject request");
    } finally {
      setLoading(false);
    }
  };

  const handleCopyPassword = (password: string) => {
    navigator.clipboard.writeText(password);
    setSuccess("Password copied to clipboard!");
    setTimeout(() => setSuccess(null), 2000);
  };

  return (
    <Box>
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Paper sx={{ p: { xs: 0, sm: 3 } }} elevation={isMobile ? 0 : 1}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant={isMobile ? "h6" : "h5"} sx={{ whiteSpace: "nowrap" }}>
            {isMobile ? "Account Requests" : "Provider Account Requests"}
          </Typography>
          <FormControl size="small" sx={{ minWidth: { xs: 110, sm: 150 }, ml: 1 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="ALL">All</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {isMobile ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {requests.length === 0 && (
              <Typography color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                No {statusFilter.toLowerCase()} requests found
              </Typography>
            )}
            {requests.map((req) => (
              <Card key={req.id} variant="outlined">
                <CardActionArea onClick={() => { setSelectedRequest(req); setOpenDetailDialog(true); }}>
                  <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 }, textAlign: "left" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {req.firstName} {req.lastName}
                        </Typography>
                        {req.practiceName && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                            {req.practiceName}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", wordBreak: "break-all" }}>
                          {req.email}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                          {formatDate(req.createdAt)}
                        </Typography>
                      </Box>
                      <Box sx={{ ml: 1, display: "flex", alignItems: "center", alignSelf: "center" }}>
                        {statusChip(req.status)}
                      </Box>
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Practice</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>NPI</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requests.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography color="text.secondary">
                        No {statusFilter.toLowerCase()} requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
                {requests.map((req) => (
                  <TableRow
                    key={req.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => { setSelectedRequest(req); setOpenDetailDialog(true); }}
                  >
                    <TableCell>{formatDate(req.createdAt)}</TableCell>
                    <TableCell>{req.firstName} {req.lastName}</TableCell>
                    <TableCell>{req.practiceName || "—"}</TableCell>
                    <TableCell>{req.email}</TableCell>
                    <TableCell>{req.npi || "—"}</TableCell>
                    <TableCell>{statusChip(req.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Detail Dialog */}
      <Dialog open={openDetailDialog} onClose={() => setOpenDetailDialog(false)} maxWidth="sm" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>
          Provider Request Details
        </DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box sx={{ mt: 1 }}>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">First Name</Typography>
                  <Typography variant="body1">{selectedRequest.firstName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Last Name</Typography>
                  <Typography variant="body1">{selectedRequest.lastName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">Clinic Name</Typography>
                  <Typography variant="body1">{selectedRequest.practiceName || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">NPI</Typography>
                  <Typography variant="body1">{selectedRequest.npi || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">State Licenses</Typography>
                  <Typography variant="body1">{selectedRequest.stateLicenses || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Address</Typography>
                  <Typography variant="body1">
                    {selectedRequest.street ? `${selectedRequest.street}, ${selectedRequest.city}, ${selectedRequest.state} ${selectedRequest.zip}` : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Office Phone</Typography>
                  <Typography variant="body1">{selectedRequest.officePhone || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Mobile Phone</Typography>
                  <Typography variant="body1">{selectedRequest.mobilePhone || "—"}</Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="body2" color="text.secondary">Fax</Typography>
                  <Typography variant="body1">{selectedRequest.fax || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedRequest.email}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Sales Rep</Typography>
                  <Typography variant="body1">{selectedRequest.salesRep || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Additional Point of Contact Information</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: "pre-line" }}>{selectedRequest.contact || "—"}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">Status</Typography>
                  <Box sx={{ mt: 0.5 }}>{statusChip(selectedRequest.status)}</Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDetailDialog(false)}>Close</Button>
          {selectedRequest?.status === "PENDING" && (
            <>
              <Button
                onClick={handleReject}
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
                disabled={loading}
              >
                Reject
              </Button>
              <Button
                onClick={handleApprove}
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
                disabled={loading}
              >
                Approve
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Password Dialog (after approval) */}
      <Dialog open={openPasswordDialog} onClose={() => setOpenPasswordDialog(false)} maxWidth="sm" fullWidth sx={{ zIndex: 1400 }}>
        <DialogTitle>Provider Account Created</DialogTitle>
        <DialogContent>
          {approvedProvider && (
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Name:</strong> {approvedProvider.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Email:</strong> {approvedProvider.email}
              </Typography>
              {emailSent ? (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mt: 2 }}>
                  Welcome email sent to {approvedProvider.email}
                </Alert>
              ) : (
                <>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    Provider account created successfully!
                  </Alert>
                  <Box sx={{ mt: 2, p: 2, bgcolor: "grey.100", borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Temporary Password:
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Chip
                        label={approvedProvider.temporary_password}
                        sx={{ fontFamily: "monospace", fontSize: "1rem" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleCopyPassword(approvedProvider.temporary_password)}
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                    <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: "block" }}>
                      Make sure to copy this password - it won't be shown again!
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={<EmailIcon />}
                    sx={{ mt: 2 }}
                    fullWidth
                    onClick={() => {
                      const subject = "Your Chemist Shop Account";
                      const body = `Hi ${approvedProvider.name},\n\nYour account on The Chemist Shop has been created.\n\nEmail: ${approvedProvider.email}\nTemporary Password: ${approvedProvider.temporary_password}\n\nPlease click the link below to log in and change your password:\nhttps://providers.thechemistshoppharmacy.com/login\n\nThank you,\nThe Chemist Shop`;
                      const mailtoUrl = `mailto:${encodeURIComponent(approvedProvider.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
                      const link = document.createElement("a");
                      link.href = mailtoUrl;
                      link.click();
                    }}
                  >
                    Send Welcome Email
                  </Button>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPasswordDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProviderRequests;
